import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import CustomInput from "../CustomInput";

// 1 sq ft = 0.092903 sq m (and the inverse). Keeping the constants here
// so the composite stays self-contained.
const SQFT_TO_SQMT = 0.092903;
const SQMT_TO_SQFT = 10.7639;

/**
 * Two number inputs (square feet + square metres) that stay in sync — typing
 * in either side updates the other. Both names are registered in RHF state
 * so the schema can validate them and the FormData submit can read them.
 *
 *   <AreaPair label="Carpet Area" nameSqft="carpetAreaSqft" nameSqmt="carpetAreaSqmt" required />
 *
 * The conversion only fires from user input — we don't mirror programmatic
 * setValue calls (RHF's `field.onChange` is what we override). This avoids
 * loops during form `reset()` on edit-mode init.
 */
const AreaPair = ({ label, nameSqft, nameSqmt, required = false, disabled = false }) => {
  const { control, setValue } = useFormContext();

  const onSqftChange = (e) => {
    const v = e.target.value;
    setValue(nameSqft, v, { shouldValidate: true, shouldDirty: true });
    setValue(
      nameSqmt,
      v === "" ? "" : (parseFloat(v) * SQFT_TO_SQMT).toFixed(2),
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const onSqmtChange = (e) => {
    const v = e.target.value;
    setValue(nameSqmt, v, { shouldValidate: true, shouldDirty: true });
    setValue(
      nameSqft,
      v === "" ? "" : (parseFloat(v) * SQMT_TO_SQFT).toFixed(2),
      { shouldValidate: true, shouldDirty: true },
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Controller
        name={nameSqft}
        control={control}
        render={({ field, fieldState }) => (
          <CustomInput
            label={`${label} (sq ft)`}
            type="number"
            required={required}
            disabled={disabled}
            value={field.value ?? ""}
            onChange={onSqftChange}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name={nameSqmt}
        control={control}
        render={({ field, fieldState }) => (
          <CustomInput
            label={`${label} (sq mt)`}
            type="number"
            required={required}
            disabled={disabled}
            value={field.value ?? ""}
            onChange={onSqmtChange}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
          />
        )}
      />
    </div>
  );
};

export default AreaPair;
